/**
 * Dados mockados do Normex para desenvolvimento frontend
 * Simula as respostas do backend FastAPI
 */

import type {
  AuthUser, Tenant, Person, User, Group, Risk, Area,
  ActionPlan, Inspection, Notification, Plan, Subscription,
  Invoice, PaymentMethod, Payment, Document as NormexDocument,
  Training, ChecklistTemplate, AuditLog, PixCharge, BoletoCharge, NfseInvoice,
  BillingSettings, ContactType, Unit,
} from '@/types';
import { ADMIN_PERMISSIONS } from '@/lib/permissions';

export const mockTenant: Tenant = {
  id: 'tenant-001',
  name: 'Empresa Demo Ltda',
  slug: 'empresa-demo',
  domain: 'demo.normex.com.br',
  logo_url: undefined,
  primary_color: '#4F46E5',
  accent_color: '#06B6D4',
  timezone: 'America/Sao_Paulo',
  locale: 'pt-BR',
  is_active: true,
  created_at: '2025-01-15T10:00:00Z',
  updated_at: '2025-01-15T10:00:00Z',
};

export const mockAuthUser: AuthUser = {
  id: 'user-001',
  tenant_id: 'tenant-001',
  tenant: mockTenant,
  email: 'admin@empresa-demo.com.br',
  name: 'Carlos Silva',
  avatar_url: undefined,
  permissions: ADMIN_PERMISSIONS,
  groups: [{ id: 'group-001', tenant_id: 'tenant-001', name: 'Administradores', is_system: true, created_at: '2025-01-15T10:00:00Z' }],
};

export const mockAreas: Area[] = [
  { id: 'area-001', tenant_id: 'tenant-001', name: 'Produção', description: 'Área de produção industrial', created_at: '2025-01-15T10:00:00Z' },
  { id: 'area-002', tenant_id: 'tenant-001', name: 'Almoxarifado', description: 'Estoque e armazenamento', created_at: '2025-01-15T10:00:00Z' },
  { id: 'area-003', tenant_id: 'tenant-001', name: 'Administrativo', description: 'Escritórios administrativos', created_at: '2025-01-15T10:00:00Z' },
  { id: 'area-004', tenant_id: 'tenant-001', name: 'Manutenção', parent_id: 'area-001', description: 'Setor de manutenção fabril', created_at: '2025-01-15T10:00:00Z' },
];

export const mockRisks: Risk[] = [
  {
    id: 'risk-001', tenant_id: 'tenant-001', area_id: 'area-001', area: mockAreas[0],
    title: 'Queda de altura em plataformas', description: 'Risco de queda em plataformas elevadas sem guarda-corpo adequado',
    classification: 'Físico', cause: 'Guarda-corpo danificado', consequence: 'Lesão grave ou óbito',
    severity: 'intoleravel', probability: 'possivel', risk_level: 20,
    status: 'em_tratamento', attachments: [], created_at: '2025-02-01T10:00:00Z', updated_at: '2025-02-01T10:00:00Z',
  },
  {
    id: 'risk-002', tenant_id: 'tenant-001', area_id: 'area-002', area: mockAreas[1],
    title: 'Exposição a produtos químicos', description: 'Contato com solventes sem EPI adequado',
    classification: 'Químico', cause: 'Falta de EPI', consequence: 'Dermatite ocupacional',
    severity: 'moderado', probability: 'provavel', risk_level: 12,
    status: 'identificado', attachments: [], created_at: '2025-02-05T10:00:00Z', updated_at: '2025-02-05T10:00:00Z',
  },
  {
    id: 'risk-003', tenant_id: 'tenant-001', area_id: 'area-001', area: mockAreas[0],
    title: 'Ruído excessivo', description: 'Níveis de ruído acima de 85dB na área de produção',
    classification: 'Físico', cause: 'Máquinas sem isolamento acústico', consequence: 'Perda auditiva induzida',
    severity: 'substancial', probability: 'quase_certa', risk_level: 16,
    status: 'monitorado', attachments: [], created_at: '2025-02-10T10:00:00Z', updated_at: '2025-02-10T10:00:00Z',
  },
  {
    id: 'risk-004', tenant_id: 'tenant-001', area_id: 'area-003', area: mockAreas[2],
    title: 'Ergonomia inadequada', description: 'Postos de trabalho sem ajuste ergonômico',
    classification: 'Ergonômico', cause: 'Mobiliário inadequado', consequence: 'LER/DORT',
    severity: 'toleravel', probability: 'provavel', risk_level: 8,
    status: 'em_analise', attachments: [], created_at: '2025-02-12T10:00:00Z', updated_at: '2025-02-12T10:00:00Z',
  },
];

export const mockActions: ActionPlan[] = [
  {
    id: 'action-001', tenant_id: 'tenant-001', risk_id: 'risk-001',
    title: 'Instalar guarda-corpo nas plataformas', description: 'Substituir guarda-corpo danificado por modelo certificado',
    responsible_id: 'user-002', status: 'em_andamento', priority: 'critica',
    due_date: '2025-03-15', comments: [], attachments: [],
    created_at: '2025-02-01T10:00:00Z', updated_at: '2025-02-01T10:00:00Z',
  },
  {
    id: 'action-002', tenant_id: 'tenant-001', risk_id: 'risk-002',
    title: 'Fornecer EPIs para área química', description: 'Adquirir e distribuir luvas nitrílicas e óculos de proteção',
    responsible_id: 'user-003', status: 'pendente', priority: 'alta',
    due_date: '2025-03-01', comments: [], attachments: [],
    created_at: '2025-02-05T10:00:00Z', updated_at: '2025-02-05T10:00:00Z',
  },
  {
    id: 'action-003', tenant_id: 'tenant-001', risk_id: 'risk-003',
    title: 'Isolamento acústico das máquinas', description: 'Instalar barreiras acústicas e protetores auriculares',
    responsible_id: 'user-002', status: 'atrasada', priority: 'alta',
    due_date: '2025-02-20', comments: [], attachments: [],
    created_at: '2025-02-10T10:00:00Z', updated_at: '2025-02-10T10:00:00Z',
  },
];

export const mockNotifications: Notification[] = [
  { id: 'notif-001', tenant_id: 'tenant-001', user_id: 'user-001', title: 'Ação atrasada', message: 'A ação "Isolamento acústico" está atrasada.', type: 'warning', is_read: false, link: '/acoes', created_at: '2025-02-25T08:00:00Z' },
  { id: 'notif-002', tenant_id: 'tenant-001', user_id: 'user-001', title: 'Nova inspeção agendada', message: 'Inspeção na área de Produção agendada para 28/02.', type: 'info', is_read: false, link: '/inspecoes', created_at: '2025-02-24T14:00:00Z' },
  { id: 'notif-003', tenant_id: 'tenant-001', user_id: 'user-001', title: 'Risco identificado', message: 'Novo risco "Ergonomia inadequada" registrado.', type: 'info', is_read: true, link: '/riscos', created_at: '2025-02-23T10:00:00Z' },
  { id: 'notif-004', tenant_id: 'tenant-001', user_id: 'user-001', title: 'Fatura vencendo', message: 'Sua fatura de Março vence em 3 dias.', type: 'warning', is_read: false, link: '/financeiro/faturas', created_at: '2025-02-22T09:00:00Z' },
];

export const mockPlans: Plan[] = [
  { id: 'plan-001', name: 'Starter', description: 'Ideal para pequenas empresas', price: 199, currency: 'BRL', billing_cycle: 'mensal', features: ['Até 50 riscos', 'Até 5 usuários', '1 GB de documentos', 'Suporte por e-mail'], limits: { users: 5, risks: 50, storage_gb: 1 }, is_active: true, created_at: '2025-01-01T00:00:00Z' },
  { id: 'plan-002', name: 'Professional', description: 'Para empresas em crescimento', price: 499, currency: 'BRL', billing_cycle: 'mensal', features: ['Até 200 riscos', 'Até 20 usuários', '10 GB de documentos', 'Suporte prioritário', 'Relatórios avançados', 'IA básica'], limits: { users: 20, risks: 200, storage_gb: 10 }, is_active: true, created_at: '2025-01-01T00:00:00Z' },
  { id: 'plan-003', name: 'Enterprise', description: 'Para grandes operações', price: 999, currency: 'BRL', billing_cycle: 'mensal', features: ['Riscos ilimitados', 'Usuários ilimitados', '100 GB de documentos', 'Suporte dedicado', 'Relatórios customizados', 'IA completa', 'API access', 'SSO'], limits: { users: -1, risks: -1, storage_gb: 100 }, is_active: true, created_at: '2025-01-01T00:00:00Z' },
];

export const mockSubscription: Subscription = {
  id: 'sub-001', tenant_id: 'tenant-001', plan_id: 'plan-002', plan: mockPlans[1],
  status: 'ativa', billing_day: 10, next_billing_date: '2025-03-10',
  created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z',
};

export const mockInvoices: Invoice[] = [
  { id: 'inv-001', tenant_id: 'tenant-001', subscription_id: 'sub-001', amount: 499, currency: 'BRL', status: 'paga', period_start: '2025-01-10', period_end: '2025-02-09', due_date: '2025-01-10', paid_at: '2025-01-10T12:00:00Z', created_at: '2025-01-10T00:00:00Z' },
  { id: 'inv-002', tenant_id: 'tenant-001', subscription_id: 'sub-001', amount: 499, currency: 'BRL', status: 'paga', period_start: '2025-02-10', period_end: '2025-03-09', due_date: '2025-02-10', paid_at: '2025-02-10T14:00:00Z', created_at: '2025-02-10T00:00:00Z' },
  { id: 'inv-003', tenant_id: 'tenant-001', subscription_id: 'sub-001', amount: 499, currency: 'BRL', status: 'pendente', period_start: '2025-03-10', period_end: '2025-04-09', due_date: '2025-03-10', created_at: '2025-03-01T00:00:00Z' },
];

export const mockPaymentMethods: PaymentMethod[] = [
  { id: 'pm-001', tenant_id: 'tenant-001', type: 'credit_card', is_default: true, card_brand: 'Visa', card_last4: '4242', card_expiry: '12/27', created_at: '2025-01-15T10:00:00Z' },
  { id: 'pm-002', tenant_id: 'tenant-001', type: 'pix', is_default: false, created_at: '2025-02-01T10:00:00Z' },
];

export const mockContactTypes: ContactType[] = [
  { id: 'ct-001', name: 'E-mail', system_code: 'EMAIL', is_system: true, created_at: '2025-01-01T00:00:00Z' },
  { id: 'ct-002', name: 'Celular', system_code: 'MOBILE', is_system: true, created_at: '2025-01-01T00:00:00Z' },
  { id: 'ct-003', name: 'WhatsApp', system_code: 'WHATSAPP', is_system: true, created_at: '2025-01-01T00:00:00Z' },
  { id: 'ct-004', name: 'Telefone', system_code: 'PHONE', is_system: true, created_at: '2025-01-01T00:00:00Z' },
  { id: 'ct-005', tenant_id: 'tenant-001', name: 'Ramal', is_system: false, created_at: '2025-01-15T10:00:00Z' },
];

export const mockPeople: Person[] = [
  { id: 'person-001', tenant_id: 'tenant-001', type: 'PF', name: 'Carlos Silva', document: '123.456.789-00', is_active: true, created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z' },
  { id: 'person-002', tenant_id: 'tenant-001', type: 'PF', name: 'Ana Souza', document: '987.654.321-00', is_active: true, created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z' },
  { id: 'person-003', tenant_id: 'tenant-001', type: 'PJ', name: 'Segurança Total Ltda', document: '12.345.678/0001-90', trade_name: 'SegTotal', is_active: true, created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z' },
];

export const mockUsers: User[] = [
  { id: 'user-001', tenant_id: 'tenant-001', person_id: 'person-001', email: 'admin@empresa-demo.com.br', name: 'Carlos Silva', is_active: true, last_login_at: '2025-02-27T08:00:00Z', created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z' },
  { id: 'user-002', tenant_id: 'tenant-001', person_id: 'person-002', email: 'ana@empresa-demo.com.br', name: 'Ana Souza', is_active: true, last_login_at: '2025-02-26T10:00:00Z', created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z' },
];

export const mockGroups: Group[] = [
  { id: 'group-001', tenant_id: 'tenant-001', name: 'Administradores', description: 'Acesso total ao sistema', is_system: true, created_at: '2025-01-15T10:00:00Z' },
  { id: 'group-002', tenant_id: 'tenant-001', name: 'Gestores', description: 'Gestão de riscos e operações', is_system: true, created_at: '2025-01-15T10:00:00Z' },
  { id: 'group-003', tenant_id: 'tenant-001', name: 'Operadores', description: 'Execução de inspeções e ações', is_system: true, created_at: '2025-01-15T10:00:00Z' },
];

export const mockUnits: Unit[] = [
  { id: 'unit-001', tenant_id: 'tenant-001', name: 'Matriz - Porto Alegre', unit_type: 'matriz', cnpj: '12.345.678/0001-90', is_active: true, created_at: '2025-01-15T10:00:00Z', updated_at: '2025-01-15T10:00:00Z' },
  { id: 'unit-002', tenant_id: 'tenant-001', name: 'Filial São Paulo', unit_type: 'filial', cnpj: '12.345.678/0002-71', is_active: true, created_at: '2025-02-01T10:00:00Z', updated_at: '2025-02-01T10:00:00Z' },
  { id: 'unit-003', tenant_id: 'tenant-001', name: 'Fábrica Manaus', unit_type: 'fabrica', cnpj: '12.345.678/0003-52', is_active: true, created_at: '2025-02-10T10:00:00Z', updated_at: '2025-02-10T10:00:00Z' },
];

export const mockUnitUsers: Record<string, string[]> = {
  'unit-001': ['user-001', 'user-002'],
  'unit-002': ['user-002'],
  'unit-003': ['user-001'],
};
